import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Leaf, Star, Phone } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { scrollToId } from "../lib/lenis";
import { PHONE_TEL } from "../data/content";

const HERO_IMG = "/images/salone-8.jpg";

const STATS = [
  { value: "30", label: "anni di esperienza" },
  { value: "100%", label: "cosmesi biologica" },
  { value: "4.9", label: "valutazione media", star: true },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] overflow-hidden bg-sage-dark grain" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
        <motion.img
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.6 }}
          src={HERO_IMG}
          alt="Ambiente spa rilassante del Centro Estetico Biovital"
          className="h-full w-full object-cover object-[center_55%]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/40 to-sage-dark/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-transparent to-transparent" />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative container-x flex min-h-[100svh] flex-col justify-end pb-24 pt-36 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mb-8 inline-flex w-fit items-center gap-2 rounded-full border border-cream/25 bg-cream/10 px-4 py-2 text-xs font-medium uppercase tracking-[0.22em] text-cream backdrop-blur-md"
          data-testid="hero-badge"
        >
          <Leaf className="h-3.5 w-3.5 text-gold-light" /> Centro Estetico Bio · Campodarsego
        </motion.div>

        <h1 className="font-serif text-5xl leading-[0.98] tracking-tight text-cream sm:text-6xl lg:text-7xl xl:text-8xl" data-testid="hero-title">
          <MaskedLines lines={["La tua bellezza,"]} delay={0.6} />
          <MaskedLines lines={["il tuo benessere"]} delay={0.74} className="italic font-normal text-beige" />
          <MaskedLines lines={["naturale."]} delay={0.88} className="text-gold-light" />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-cream/80 md:text-lg"
          data-testid="hero-subtitle"
        >
          Nel cuore di Reschigliano di Campodarsego, un centro estetico dove la cosmesi biologica incontra
          tecnologie avanzate e mani esperte. Trattamenti personalizzati, ritmi lenti, risultati veri.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <a href={PHONE_TEL} className="btn-gold" data-testid="hero-book-btn">
            <Phone className="h-4 w-4" /> Chiama per prenotare
          </a>
          <button onClick={() => scrollToId("servizi")} className="btn-outline text-cream hover:bg-cream hover:text-ink" data-testid="hero-services-btn">
            Scopri i trattamenti
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-cream/15 pt-8"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="flex items-center gap-1 font-serif text-3xl text-cream md:text-4xl">
                {s.value}
                {s.star && <Star className="h-4 w-4 fill-gold-light text-gold-light" />}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-cream/60">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("chi-siamo")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="absolute bottom-8 right-6 hidden items-center gap-3 text-cream/70 md:flex hover:text-cream transition-colors"
        aria-label="Scorri"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scorri</span>
        <span className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/30 animate-float">
          <ArrowDown className="h-4 w-4" />
        </span>
      </motion.button>
    </section>
  );
};
